"use client";
// @ts-strict-ignore
import * as React from "react";
import useSWR, { useSWRConfig } from "swr";
import { BlogAPI } from "services/api/blog";
import { FIRST_PAGE_SWR_KEY } from "constants/blog";
import { Heading, ResponsiveBody } from "components/parts/Typography";
import type { BlogHomePageProps } from "types/blog-type";
import {
  BlogHomepageBackground,
  BlogHomepageGrid,
  ContributorsAndSubscribeSection,
  HeaderContainer,
  PostsSection,
  SubscribeContainer,
  SubscribeImgContainer,
  SubscribeInfoContainer,
  SubscribeTextContainer
} from "./style";
import BlogSubscribePlaceholder from "public/images/blog/blog-subscribe-placeholder.svg";
import FeaturedPost from "./FeaturedPost";
import PostsGrid from "./PostsGrid";
import FeaturedContributors from "./FeaturedContributorsList";
import EmailSubmit, {
  SUBSCRIPTION_TYPE_GENERAL
} from "components/parts/EmailSubmit";
import { EmailInput } from "./EmailInput";
import Pagination from "./Pagination";
import CategoriesBar from "./CategoriesBar";
import { useSearchParams } from "next/navigation";
import { decoratePosts, getSWRKeyFomPage } from "utils/blog";
import { useTranslations } from "i18n";
import { FILES } from "i18n/constants";
import Image from "next/image";
import { POSTS_FIRST_PAGE, POSTS_PER_PAGE } from "constants/blog";
import { PaginationSkeleton } from "./Categories/LoadingSkeleton";

const blogPostsFetcher = async (
  page: string,
  docIdsToExclude: string[],
  fallback: {
    [key: string]: any;
  }
) => {
  if (page === FIRST_PAGE_SWR_KEY) {
    return fallback[page];
  }

  const fetchedPosts = await BlogAPI.fetchBlogPosts(
    parseInt(page),
    docIdsToExclude
  );
  return decoratePosts(fetchedPosts);
};

export const useBlogPostsFetcher = (
  page: number,
  docIdsToExclude: string[]
) => {
  const { fallback } = useSWRConfig();
  const { data, error, isLoading } = useSWR(
    getSWRKeyFomPage(page),
    (key) => blogPostsFetcher(key, docIdsToExclude, fallback),
    {
      // caching the visited pages' to avoid refetching from Contentful API
      dedupingInterval: 20000
    }
  );

  return {
    data,
    error,
    isLoading
  };
};

const BlogHomePage = ({
  blogCategories,
  featuredPost,
  featuredAuthors,
  smallFeaturedPosts,
  totalPosts
}: Omit<BlogHomePageProps, "initialPosts">) => {
  const t = useTranslations(FILES.BLOG);
  const [hasSubscribed, setHasSubscribed] = React.useState<boolean>(false);

  const searchParams = useSearchParams();
  let currentPage = parseInt(searchParams?.get("page") ?? "1") - 1;

  const calculateTotalPages = React.useMemo(() => {
    return Math.ceil((totalPosts - POSTS_FIRST_PAGE) / POSTS_PER_PAGE);
  }, [totalPosts]);

  if (currentPage >= calculateTotalPages) currentPage = calculateTotalPages - 1;

  const { data: posts, isLoading } = useBlogPostsFetcher(
    currentPage,
    smallFeaturedPosts.concat(featuredPost).map((post) => post.id)
  );

  // Render prop for <EmailSubmit/> component
  const emailInputRender = React.useCallback(
    (props) => <EmailInput {...props} />,
    []
  );
  // Callback to invoke after successful email subscription - unmounts email input component
  const onSubscribe = React.useCallback(() => setHasSubscribed(true), []);

  return (
    <BlogHomepageBackground>
      <BlogHomepageGrid>
        <HeaderContainer justifyContent="center">
          <Heading mt="32px" mb="24px" size="md" as="h1">
            {t("homepageTitle")}
          </Heading>
        </HeaderContainer>
        <CategoriesBar blogCategories={blogCategories} />
        {currentPage === 0 ? (
          <PostsSection justifyContent="center">
            <FeaturedPost featuredPost={featuredPost} />
            <PostsGrid
              title={t("topStoriesTitle")}
              posts={smallFeaturedPosts}
            />
            <ContributorsAndSubscribeSection>
              <SubscribeContainer>
                <SubscribeImgContainer>
                  <Image
                    src={BlogSubscribePlaceholder}
                    alt={t("subscribeAlt")}
                    width={300}
                    height={150}
                  />
                </SubscribeImgContainer>
                <SubscribeInfoContainer>
                  {!hasSubscribed && (
                    <SubscribeTextContainer>
                      <Heading size="md" m="0" mt="4px">
                        {t("subscribeTitle")}
                      </Heading>
                      <ResponsiveBody m="0" mt="8px" mb="24px">
                        {t("subscribeText")}
                      </ResponsiveBody>
                    </SubscribeTextContainer>
                  )}
                  <EmailSubmit
                    subscriptionType={SUBSCRIPTION_TYPE_GENERAL}
                    onSubscribe={onSubscribe}
                    renderProp={emailInputRender}
                  />
                </SubscribeInfoContainer>
              </SubscribeContainer>
              <FeaturedContributors
                title={t("contributorsTitle")}
                featuredAuthors={featuredAuthors}
              />
            </ContributorsAndSubscribeSection>
            {posts && (
              <PostsGrid
                title={t("moreArticlesTitle", { author: "Gemini" })}
                posts={posts.slice(0, Math.min(posts.length, POSTS_FIRST_PAGE))}
              />
            )}
          </PostsSection>
        ) : (
          <PostsSection justifyContent="center">
            {isLoading && <PaginationSkeleton />}
            {!isLoading && posts && (
              <PostsGrid
                posts={posts.slice(0, Math.min(posts.length, POSTS_PER_PAGE))}
              />
            )}
          </PostsSection>
        )}
      </BlogHomepageGrid>
      <React.Suspense>
        <Pagination
          totalPages={calculateTotalPages}
          currentPage={currentPage}
        />
      </React.Suspense>
    </BlogHomepageBackground>
  );
};

export default BlogHomePage;
